import React from 'react';

const LoadingSpinner = ({ size = 'md', text = 'Loading...', className = '' }) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16'
  };

  const textSizes = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
    xl: 'text-lg'
  };

  return (
    <div className={`flex flex-col items-center justify-center space-y-4 ${className}`}>
      <div className="relative">
        {/* Main spinner */}
        <div className={`${sizeClasses[size]} animate-spin rounded-full border-2 border-gray-200 dark:border-gray-700`}>
          <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-primary-600"></div>
        </div>
        
        {/* Pulse effect */}
        <div className={`absolute inset-0 ${sizeClasses[size]} animate-pulse rounded-full bg-primary-100 dark:bg-primary-900/20`}></div>
      </div>
      
      {text && (
        <div className="text-center">
          <p className={`${textSizes[size]} font-medium text-gray-600 dark:text-gray-400`}>
            {text}
          </p>
          <div className="flex justify-center space-x-1 mt-2">
            <div className="w-1 h-1 bg-primary-400 rounded-full animate-pulse"></div>
            <div className="w-1 h-1 bg-primary-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
            <div className="w-1 h-1 bg-primary-400 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
          </div>
        </div>
      )}
    </div>
  );
};

// Professional loading skeleton component
export const LoadingSkeleton = ({ className = '', lines = 3, height = 'h-4' }) => {
  return (
    <div className={`space-y-3 ${className}`}>
      {Array.from({ length: lines }).map((_, index) => (
        <div
          key={index}
          className={`${height} bg-gray-200 dark:bg-gray-700 rounded animate-shimmer`}
          style={{ animationDelay: `${index * 0.1}s` }}
        ></div>
      ))}
    </div>
  );
};

// Professional card skeleton
export const CardSkeleton = ({ className = '' }) => {
  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200/60 dark:border-gray-700/60 shadow-sm p-6 ${className}`}>
      <div className="flex items-center space-x-4 mb-4">
        <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-lg animate-shimmer"></div>
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-shimmer"></div>
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/4 animate-shimmer"></div>
        </div>
      </div>
      <div className="space-y-3">
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-shimmer"></div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6 animate-shimmer"></div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-4/6 animate-shimmer"></div>
      </div>
    </div>
  );
};

// Professional table skeleton
export const TableSkeleton = ({ rows = 5, columns = 4, className = '' }) => {
  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200/60 dark:border-gray-700/60 shadow-sm overflow-hidden ${className}`}>
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200/60 dark:border-gray-700/60">
        <div className="flex space-x-4">
          {Array.from({ length: columns }).map((_, index) => (
            <div
              key={index}
              className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-shimmer"
              style={{ width: `${Math.random() * 40 + 20}%` }}
            ></div>
          ))}
        </div>
      </div>
      
      {/* Rows */}
      <div className="divide-y divide-gray-200/60 dark:divide-gray-700/60">
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div key={rowIndex} className="px-6 py-4">
            <div className="flex space-x-4">
              {Array.from({ length: columns }).map((_, colIndex) => (
                <div
                  key={colIndex}
                  className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-shimmer"
                  style={{ 
                    width: `${Math.random() * 40 + 20}%`,
                    animationDelay: `${(rowIndex + colIndex) * 0.1}s`
                  }}
                ></div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Professional chart skeleton
export const ChartSkeleton = ({ className = '' }) => {
  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200/60 dark:border-gray-700/60 shadow-sm p-6 ${className}`}>
      <div className="flex items-center justify-between mb-6">
        <div className="space-y-2">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded animate-shimmer w-32"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-shimmer w-48"></div>
        </div>
        <div className="h-8 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-shimmer"></div>
      </div>
      
      <div className="h-64 bg-gray-100 dark:bg-gray-700 rounded-lg animate-shimmer relative overflow-hidden">
        {/* Chart bars */}
        <div className="absolute inset-0 flex items-end justify-around px-4 pb-4">
          {Array.from({ length: 8 }).map((_, index) => (
            <div
              key={index}
              className="w-8 bg-gray-300 dark:bg-gray-600 rounded-t animate-shimmer"
              style={{ 
                height: `${Math.random() * 60 + 20}%`,
                animationDelay: `${index * 0.1}s`
              }}
            ></div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LoadingSpinner; 